import moment from 'moment'

// Shared membership-list helpers for the admin Membership dialog and the
// member-facing membership page.
//
// WHY THIS EXISTS: `lib/subscriptions.subscriptionsByState` collapses to a
// single `pending` slot, and the loop is last-wins — so a member with memberships
// stacked back to back gets all but the last upcoming one silently dropped. Both
// screens have to list every Pending membership themselves; keeping that in one
// place stops the two from drifting.

const PERIOD_BY_BILLING_PERIOD = {
  monthly: { n: 1, unit: 'months' },
  '2month': { n: 2, unit: 'months' },
  '3month': { n: 3, unit: 'months' },
  '9month': { n: 9, unit: 'months' },
  annual: { n: 1, unit: 'year' },
}

// planId is the fallback for records written before `billingPeriod` was stamped
// on the subscription. Order matters only in that no plan id contains two of
// these tokens.
const periodFromPlanId = planId => {
  if (!planId) return null
  if (planId.includes('9month')) return { n: 9, unit: 'months' }
  if (planId.includes('3month')) return { n: 3, unit: 'months' }
  if (planId.includes('2month')) return { n: 2, unit: 'months' }
  if (planId.includes('1month')) return { n: 1, unit: 'months' }
  if (planId.includes('annual')) return { n: 1, unit: 'year' }

  return null
}

export const membershipPeriod = subscription =>
  PERIOD_BY_BILLING_PERIOD[subscription?.billingPeriod] || periodFromPlanId(subscription?.planId)

/**
 * The day a membership's coverage ends, as a moment (or null if it can't be
 * determined). A Pending membership has no `paidThroughDate` yet — it is only
 * stamped when the billing cycle runs on its start date — so project it from
 * the start date plus the plan's period.
 */
export const membershipEndDate = subscription => {
  if (!subscription) return null
  if (subscription.paidThroughDate) return moment(subscription.paidThroughDate)

  const period = membershipPeriod(subscription)
  if (!period || !subscription.firstBillingDate) return null

  return moment(subscription.firstBillingDate).add(period.n, period.unit).subtract(1, 'day')
}

/**
 * Every not-yet-started membership in the given array, soonest first.
 */
export const upcomingMemberships = subscriptions =>
  (subscriptions || [])
    .filter(s => s?.status === 'Pending')
    .slice()
    .sort((a, b) => (a.firstBillingDate || '').localeCompare(b.firstBillingDate || ''))

/**
 * The message shown alongside a membership. Own memberships carry
 * `membershipMessage`; group-sourced ones inherit the group's.
 */
export const membershipMessage = subscription => {
  const message = subscription?.membershipMessage
    || subscription?.sourceGroupMembershipMessage
    || subscription?.discountGroupMembershipMessage

  return message ? message.charAt(0).toUpperCase() + message.slice(1) : ''
}
